import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Color,
  type Mesh,
  type MeshBasicMaterial,
  type MeshStandardMaterial,
  SphereGeometry,
} from 'three';
import type { Graph3DNodeRendererProps } from '../../core/renderer.types';
import { getDepthRenderOrder } from '../renderOrder';
import { NodeTextHitPlane } from './NodeTextHitPlane';

const HTML_CONTENT_Z_INDEX_RANGE: [number, number] = [160, 0];
const VISUAL_DEBUG_PREFIX = '[Graph3DVisualDebug]';
const INITIALIZATION_FADE_DEBUG_PREFIX = '[Graph3DInitializationFadeDebug]';
const PROCESS_PULSE_RENDER_ORDER_OFFSET = 22;
const PROCESS_BODY_RENDER_ORDER_OFFSET = 24;
const PROCESS_RING_RENDER_ORDER_OFFSET = 26;
const HOVER_LEAVE_GRACE_MS = 64;
const BODY_SPHERE_GEOMETRY = new SphereGeometry(1, 24, 18);
const SHELL_SPHERE_GEOMETRY = new SphereGeometry(1, 18, 14);

declare global {
  interface Window {
    __GRAPH3D_VISUAL_DEBUG_IDS?: string[];
    __GRAPH3D_INITIALIZATION_FADE_DEBUG__?: boolean;
  }
}

function getNodeDataBoolean(data: unknown, key: string) {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const value = (data as Record<string, unknown>)[key];
  return value === true;
}

function getNodeDataString(data: unknown, key: string, fallback = '') {
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : fallback;
}

function getNodeDataBooleanOrFallback(
  data: unknown,
  key: string,
  fallback: boolean
) {
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'boolean' ? value : fallback;
}

function getNodeMetaUserData(meta: unknown) {
  if (!meta || typeof meta !== 'object') {
    return undefined;
  }
  const value = (meta as Record<string, unknown>).userData;
  return value && typeof value === 'object' ? value : undefined;
}

function clampRevealProgress(progress: number | undefined) {
  if (typeof progress !== 'number') {
    return 1;
  }
  return Math.min(Math.max(progress, 0), 1);
}

function shouldDebugNode(nodeId: string) {
  if (typeof window === 'undefined') {
    return false;
  }
  const ids = window.__GRAPH3D_VISUAL_DEBUG_IDS;
  return Array.isArray(ids) && ids.includes(nodeId);
}

export function HierarchyDotNode3D({
  node,
  runtime,
  theme,
  hovered,
  selected,
  focused,
  hoverEnabled,
  selectionEnabled,
  focusEnabled,
  onHover,
  onSelect,
  onFocus,
  onDoubleClick,
}: Graph3DNodeRendererProps) {
  const status = runtime?.status ?? 'idle';
  const isRoot = getNodeDataBoolean(node.data, 'isRoot');
  const numbering = getNodeDataString(node.data, 'numbering');
  const processName = getNodeDataString(
    node.data,
    'processName',
    getNodeDataString(node.data, 'originalName', node.label)
  );
  const hoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stableHovered, setStableHovered] = useState(false);
  const themeColor = getNodeDataString(
    node.data,
    'themeColor',
    theme.runtime[status] ?? theme.node.border
  );
  const hideLabel = getNodeDataBoolean(node.data, 'hideLabel');
  const dotRadius = Math.max(
    4,
    Math.min(node.resolvedSize.width, node.resolvedSize.height) / 2
  );
  const active =
    runtime?.active ||
    runtime?.highlighted ||
    stableHovered ||
    selected ||
    focused;
  const revealProgress = clampRevealProgress(runtime?.progress);
  const initializationFadeDebugMarkRef = useRef(-1);
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      window.__GRAPH3D_INITIALIZATION_FADE_DEBUG__ !== true
    ) {
      return;
    }

    if (typeof runtime?.progress !== 'number') {
      if (initializationFadeDebugMarkRef.current >= 0) {
        console.info(
          INITIALIZATION_FADE_DEBUG_PREFIX,
          'renderer-progress-missing',
          {
            nodeId: node.id,
            nodeLabel: node.label,
            effectiveProgress: revealProgress,
            hidden: runtime?.hidden ?? false,
            status: runtime?.status ?? 'idle',
          }
        );
        initializationFadeDebugMarkRef.current = -1;
      }
      return;
    }

    if (revealProgress <= 0.001) {
      initializationFadeDebugMarkRef.current = -1;
    }
    const mark =
      revealProgress >= 0.999
        ? 1
        : revealProgress >= 0.75
          ? 0.75
          : revealProgress >= 0.5
            ? 0.5
            : revealProgress >= 0.25
              ? 0.25
              : 0;
    if (mark <= initializationFadeDebugMarkRef.current) {
      return;
    }
    initializationFadeDebugMarkRef.current = mark;
    console.info(INITIALIZATION_FADE_DEBUG_PREFIX, 'renderer-progress', {
      nodeId: node.id,
      nodeLabel: node.label,
      progress: Number(revealProgress.toFixed(4)),
      hidden: runtime?.hidden ?? false,
      status: runtime?.status ?? 'idle',
    });
  }, [
    node.id,
    node.label,
    revealProgress,
    runtime?.hidden,
    runtime?.progress,
    runtime?.status,
  ]);
  const shouldHideNodeCompletely =
    runtime?.hidden === true && revealProgress <= 0.001;
  const showLabel =
    !hideLabel &&
    revealProgress > 0.02 &&
    (isRoot ||
      stableHovered ||
      selected ||
      focused ||
      runtime?.status === 'running');
  const fill = isRoot ? (active ? '#111827' : '#374151') : themeColor;
  const border = isRoot ? '#0f172a' : themeColor;
  const labelColor = isRoot ? '#111827' : '#334155';
  const screenAlignedLabel = getNodeDataBooleanOrFallback(
    node.data,
    'screenAlignedLabel',
    false
  );
  const displayLabel = useMemo(() => {
    if (isRoot) {
      return processName || node.label;
    }
    if (!processName) {
      return node.label;
    }
    if (!numbering) {
      return processName;
    }
    return `${numbering} ${processName}`;
  }, [isRoot, node.label, numbering, processName]);
  const labelOffsetY = isRoot ? dotRadius + 22 : -(dotRadius + 20);
  const labelHitPlaneWidth = isRoot ? 220 : 156;
  const labelHitPlaneHeight = useMemo(() => {
    const estimatedCharsPerLine = isRoot ? 18 : 12;
    const lineHeight = isRoot ? 17 : 14;
    const verticalPadding = isRoot ? 14 : 12;
    const estimatedLineCount = Math.max(
      1,
      Math.min(4, Math.ceil(displayLabel.length / estimatedCharsPerLine))
    );
    return verticalPadding + estimatedLineCount * lineHeight;
  }, [displayLabel.length, isRoot]);
  const pulseRef = useRef<Mesh>(null);
  const impactRef = useRef<Mesh>(null);
  const bodyRef = useRef<Mesh>(null);
  const ringRef = useRef<Mesh>(null);
  const pulseMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const impactMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const bodyMaterialRef = useRef<MeshStandardMaterial | null>(null);
  const ringMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const pulseRenderOrder = getDepthRenderOrder(
    node.depth,
    PROCESS_PULSE_RENDER_ORDER_OFFSET
  );
  const bodyRenderOrder = getDepthRenderOrder(
    node.depth,
    PROCESS_BODY_RENDER_ORDER_OFFSET
  );
  const ringRenderOrder = getDepthRenderOrder(
    node.depth,
    PROCESS_RING_RENDER_ORDER_OFFSET
  );
  const impactColor = useMemo(
    () => new Color(border).lerp(new Color('#ffffff'), 0.46),
    [border]
  );
  const showRunningAnimation = runtime?.status === 'running' && !isRoot;
  const showPulseShell = showRunningAnimation || active || isRoot;
  const showRingShell = showRunningAnimation || active || isRoot;
  const pulseBaseScale = dotRadius * 1.55;
  const impactBaseScale = dotRadius * 1.92;
  const bodyBaseScale = dotRadius;
  const ringBaseScale = dotRadius * 1.08;

  useEffect(() => {
    if (hovered) {
      if (hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
      if (!stableHovered) {
        setStableHovered(true);
      }
      return;
    }
    if (!stableHovered) {
      return;
    }
    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
    }
    hoverLeaveTimerRef.current = setTimeout(() => {
      hoverLeaveTimerRef.current = null;
      setStableHovered(false);
    }, HOVER_LEAVE_GRACE_MS);
  }, [hovered, stableHovered]);

  useEffect(
    () => () => {
      if (hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!shouldDebugNode(node.id)) {
      return;
    }
    console.info(VISUAL_DEBUG_PREFIX, 'node-visual-state', {
      id: node.id,
      label: node.label,
      status,
      hovered,
      selected,
      focused,
      runtimeActive: runtime?.active ?? false,
      runtimeHighlighted: runtime?.highlighted ?? false,
      hidden: runtime?.hidden ?? false,
      active,
      themeColor,
      fill,
      border,
      position: node.resolvedPosition,
      size: node.resolvedSize,
    });
  }, [
    active,
    border,
    bodyBaseScale,
    fill,
    focused,
    hideLabel,
    hovered,
    impactBaseScale,
    impactColor,
    node.id,
    node.label,
    node.resolvedPosition,
    node.resolvedSize,
    pulseBaseScale,
    ringBaseScale,
    runtime?.active,
    runtime?.hidden,
    runtime?.highlighted,
    selected,
    showRunningAnimation,
    status,
    themeColor,
  ]);

  useFrame(({ clock }) => {
    if (!showRunningAnimation) {
      const revealOpacity = (runtime?.hidden ? 0.18 : 1) * revealProgress;
      const ringOpacity = (active ? 0.94 : isRoot ? 0.4 : 0.3) * revealProgress;
      const pulseOpacity =
        (stableHovered || selected || focused ? 0.22 : isRoot ? 0.16 : 0.08) *
        revealProgress;
      if (pulseMaterialRef.current) {
        pulseMaterialRef.current.opacity = pulseOpacity;
      }
      if (bodyMaterialRef.current) {
        bodyMaterialRef.current.opacity = revealOpacity;
      }
      if (ringMaterialRef.current) {
        ringMaterialRef.current.opacity = ringOpacity;
      }
      if (pulseRef.current) {
        pulseRef.current.scale.setScalar(pulseBaseScale);
      }
      if (impactRef.current) {
        impactRef.current.scale.setScalar(impactBaseScale);
      }
      if (bodyRef.current) {
        bodyRef.current.scale.setScalar(bodyBaseScale);
      }
      if (ringRef.current) {
        ringRef.current.scale.setScalar(ringBaseScale);
      }
      return;
    }
    const elapsed = clock.getElapsedTime();
    const beat = (Math.sin(elapsed * 4.2) + 1) / 2;
    const burst = (Math.sin(elapsed * 8.4) + 1) / 2;
    const shock = (elapsed * 1.2) % 1;
    if (pulseRef.current) {
      pulseRef.current.scale.setScalar(
        pulseBaseScale * (1.08 + beat * 0.28 + burst * 0.08)
      );
    }
    if (impactRef.current) {
      impactRef.current.scale.setScalar(
        impactBaseScale * (1.02 + shock * 0.92)
      );
    }
    if (bodyRef.current) {
      bodyRef.current.scale.setScalar(
        bodyBaseScale * (1 + beat * 0.08 + burst * 0.03)
      );
    }
    if (ringRef.current) {
      ringRef.current.scale.setScalar(
        ringBaseScale * (1.02 + beat * 0.16 + burst * 0.04)
      );
    }
    if (pulseMaterialRef.current) {
      pulseMaterialRef.current.opacity = Math.min(
        0.88,
        0.34 + beat * 0.28 + burst * 0.12
      );
      pulseMaterialRef.current.color.copy(impactColor);
    }
    if (impactMaterialRef.current) {
      impactMaterialRef.current.opacity = (1 - shock) * 0.42;
      impactMaterialRef.current.color.copy(impactColor);
    }
    if (bodyMaterialRef.current) {
      bodyMaterialRef.current.emissive.copy(impactColor);
      bodyMaterialRef.current.emissiveIntensity =
        0.52 + beat * 0.52 + burst * 0.18;
    }
    if (ringMaterialRef.current) {
      ringMaterialRef.current.opacity = Math.min(
        1,
        0.56 + beat * 0.34 + burst * 0.08
      );
      ringMaterialRef.current.color.copy(impactColor);
    }
  });

  const haloOpacity = useMemo(() => {
    if (showRunningAnimation) {
      return 0.42;
    }
    if (stableHovered || selected || focused) {
      return 0.22;
    }
    return isRoot ? 0.16 : 0.08;
  }, [focused, isRoot, selected, showRunningAnimation, stableHovered]);

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!selectionEnabled && !focusEnabled) {
      return;
    }
    event.stopPropagation();
    if (selectionEnabled) {
      onSelect(node.id);
    }
    if (focusEnabled) {
      onFocus(node.id);
    }
  };

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!onDoubleClick) {
      return;
    }
    event.stopPropagation();
    onDoubleClick?.(node.id);
  };

  if (shouldHideNodeCompletely) {
    return null;
  }

  return (
    <group
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      userData={getNodeMetaUserData(node.meta)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onPointerEnter={event => {
        if (!hoverEnabled) {
          return;
        }
        event.stopPropagation();
        onHover(node.id);
      }}
      onPointerLeave={event => {
        if (!hoverEnabled) {
          return;
        }
        event.stopPropagation();
        onHover(null);
      }}
    >
      {showPulseShell && (
        <mesh
          ref={pulseRef}
          position={[0, 0, -0.8]}
          renderOrder={pulseRenderOrder}
          scale={pulseBaseScale}
        >
          <primitive object={SHELL_SPHERE_GEOMETRY} attach="geometry" />
          <meshBasicMaterial
            ref={pulseMaterialRef}
            color={showRunningAnimation ? impactColor : border}
            transparent
            opacity={haloOpacity * revealProgress}
          />
        </mesh>
      )}
      {showRunningAnimation && (
        <mesh
          ref={impactRef}
          position={[0, 0, -1.1]}
          renderOrder={pulseRenderOrder - 1}
          scale={impactBaseScale}
        >
          <primitive object={SHELL_SPHERE_GEOMETRY} attach="geometry" />
          <meshBasicMaterial
            ref={impactMaterialRef}
            color={impactColor}
            transparent
            opacity={0.24 * revealProgress}
            wireframe
          />
        </mesh>
      )}
      <mesh ref={bodyRef} renderOrder={bodyRenderOrder} scale={bodyBaseScale}>
        <primitive object={BODY_SPHERE_GEOMETRY} attach="geometry" />
        <meshStandardMaterial
          ref={bodyMaterialRef}
          color={fill}
          emissive={showRunningAnimation ? impactColor : border}
          emissiveIntensity={
            showRunningAnimation ? 0.52 : active ? 0.3 : isRoot ? 0.12 : 0.08
          }
          roughness={0.34}
          metalness={isRoot ? 0.18 : 0.05}
          transparent={runtime?.hidden === true || revealProgress < 0.999}
          opacity={(runtime?.hidden ? 0.18 : 1) * revealProgress}
        />
      </mesh>
      {showRingShell && (
        <mesh ref={ringRef} renderOrder={ringRenderOrder} scale={ringBaseScale}>
          <primitive object={SHELL_SPHERE_GEOMETRY} attach="geometry" />
          <meshBasicMaterial
            ref={ringMaterialRef}
            color={showRunningAnimation ? impactColor : border}
            transparent
            opacity={
              (showRunningAnimation
                ? 0.56
                : active
                  ? 0.94
                  : isRoot
                    ? 0.4
                    : 0.3) * revealProgress
            }
            wireframe
          />
        </mesh>
      )}
      {showLabel && (
        <NodeTextHitPlane
          width={labelHitPlaneWidth}
          height={labelHitPlaneHeight}
          y={labelOffsetY}
          z={dotRadius + 8}
        />
      )}
      {showLabel && (
        <Html
          center
          transform={!screenAlignedLabel}
          sprite={screenAlignedLabel ? false : undefined}
          distanceFactor={screenAlignedLabel ? undefined : 400}
          zIndexRange={HTML_CONTENT_Z_INDEX_RANGE}
          position={[0, labelOffsetY, dotRadius + 8]}
        >
          <div
            style={{
              maxWidth: isRoot ? 220 : 156,
              width: isRoot ? 'max-content' : 156,
              minWidth: isRoot ? undefined : 96,
              padding: isRoot ? '6px 10px' : '4px 8px',
              borderRadius: 999,
              border: `1px solid ${isRoot ? 'rgba(15,23,42,0.16)' : 'rgba(148,163,184,0.26)'}`,
              background: 'rgba(255,255,255,0.9)',
              boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
              color: labelColor,
              fontFamily:
                'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
              fontSize: isRoot ? 13 : 11,
              fontWeight: isRoot ? 700 : 600,
              lineHeight: isRoot ? '17px' : '14px',
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              opacity: revealProgress,
              pointerEvents: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
          >
            {displayLabel}
          </div>
        </Html>
      )}
    </group>
  );
}
